import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Heart, AlertTriangle, Ban, CheckCircle2 } from 'lucide-react';

const CodeOfConduct = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFCC00] rounded-full mb-6">
            <Heart className="h-8 w-8 text-black" />
          </div>
          <h1 className="text-5xl font-bold font-montserrat mb-4">
            Code of <span className="text-[#FFCC00]">Conduct</span>
          </h1>
          <p className="text-gray-400 text-lg">For the Millionaire Café & Live Review Sessions</p>
          <p className="text-gray-400 text-sm mt-2">Last Updated: January 23, 2025</p>
        </div>

        <Card className="bg-gray-800/50 border-gray-700 p-8 backdrop-blur-sm">
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">Our Commitment</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Mic Check Millionaire is built on respect, creativity, and community. The Millionaire Café and live review sessions are spaces where artists and reviewers connect, grow, and support each other.
            </p>
            <p className="text-gray-300 leading-relaxed">
              This Code of Conduct outlines the behavior we expect from all members to maintain a positive, professional, and inclusive environment.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Zero Tolerance */}
          <section className="mb-8">
            <div className="flex items-start mb-4">
              <Ban className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold font-montserrat text-red-500">Zero Tolerance Policy</h2>
            </div>
            <div className="bg-red-900/20 border-2 border-red-700 rounded-lg p-6 mb-6">
              <p className="text-gray-300 leading-relaxed mb-4">
                <strong className="text-white">We have ZERO tolerance for:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                <li><strong>Hate Speech:</strong> Any language that attacks or demeans based on race, ethnicity, religion, gender, sexual orientation, disability, or any other protected characteristic</li>
                <li><strong>Harassment:</strong> Unwanted contact, stalking, intimidation, threats, or abusive behavior</li>
                <li><strong>Discrimination:</strong> Treating someone unfairly because of their identity or background</li>
                <li><strong>Bullying:</strong> Repeated hostile behavior intended to harm, intimidate, or humiliate others</li>
                <li><strong>Sexual Content:</strong> Explicit, inappropriate, or unwanted sexual comments, images, or advances</li>
                <li><strong>Doxxing:</strong> Sharing someone&apos;s personal information without consent</li>
              </ul>
            </div>
            <p className="text-gray-300 leading-relaxed">
              <strong className="text-red-400">Violations will result in immediate suspension or permanent ban</strong> from the platform. We do not issue warnings for severe violations.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Artist Responsibilities */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">For Artists</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white flex items-center">
                  <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
                  Content Ownership
                </h3>
                <p className="text-gray-300 leading-relaxed ml-7">
                  You must only upload music that you own or have explicit permission to share. Uploading copyrighted content without authorization is strictly prohibited and may result in account termination.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2 text-white flex items-center">
                  <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
                  Accepting Feedback
                </h3>
                <p className="text-gray-300 leading-relaxed ml-7">
                  Reviews are meant to help you grow. Accept constructive criticism professionally. Disagreement is okay, but aggression, insults, or harassment toward reviewers will not be tolerated.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2 text-white flex items-center">
                  <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
                  Respecting Queue Rules
                </h3>
                <p className="text-gray-300 leading-relaxed ml-7">
                  Do not spam submissions or attempt to manipulate the queue. Follow the reviewer&apos;s session rules and respect their time.
                </p>
              </div>
            </div>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Reviewer Responsibilities */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">For Reviewers</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white flex items-center">
                  <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
                  Professionalism is Key
                </h3>
                <p className="text-gray-300 leading-relaxed ml-7">
                  Provide honest, constructive feedback. Your goal is to help artists improve, not to tear them down. Avoid trolling, mockery, or disrespectful comments.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2 text-white flex items-center">
                  <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
                  No Bias or Exploitation
                </h3>
                <p className="text-gray-300 leading-relaxed ml-7">
                  Treat all artists fairly regardless of their skill level, genre, or background. Do not use your platform to exploit, scam, or manipulate artists.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2 text-white flex items-center">
                  <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
                  Content Integrity
                </h3>
                <p className="text-gray-300 leading-relaxed ml-7">
                  You do not own the music you review. Do not download, redistribute, or claim ownership of artists&apos; work without permission.
                </p>
              </div>
            </div>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Community Standards */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">Community Standards</h2>
            <div className="space-y-4 text-gray-300">
              <p className="leading-relaxed">
                The Millionaire Café is a space for creators to connect, learn, and support each other. Whether you&apos;re posting, commenting, or reacting, we expect:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Respect:</strong> Treat everyone with kindness and dignity</li>
                <li><strong>Constructive Dialogue:</strong> Share opinions without attacking others</li>
                <li><strong>Positive Energy:</strong> Celebrate wins, offer support during challenges</li>
                <li><strong>No Spam:</strong> Don&apos;t flood the forum with promotional content or irrelevant posts</li>
                <li><strong>Stay On Topic:</strong> Keep discussions relevant to music, reviews, and the community</li>
              </ul>
            </div>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Encouraging Behavior */}
          <section className="mb-8">
            <div className="flex items-start mb-4">
              <Heart className="h-6 w-6 text-[#FFCC00] mr-3 flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold font-montserrat text-[#FFCC00]">We Encourage</h2>
            </div>
            <div className="bg-green-900/20 border-2 border-green-700 rounded-lg p-6">
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li><strong>Constructive Feedback:</strong> Help others grow with actionable advice</li>
                <li><strong>Mutual Support:</strong> Celebrate achievements and offer encouragement</li>
                <li><strong>Collaboration:</strong> Connect with other creators and build together</li>
                <li><strong>Open-Mindedness:</strong> Listen to different perspectives and genres</li>
                <li><strong>Authenticity:</strong> Be genuine and bring your real self to the community</li>
                <li><strong>Growth Mindset:</strong> See feedback as an opportunity to improve</li>
              </ul>
            </div>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Enforcement */}
          <section className="mb-8">
            <div className="flex items-start mb-4">
              <AlertTriangle className="h-6 w-6 text-yellow-500 mr-3 flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold font-montserrat text-yellow-500">Enforcement & Consequences</h2>
            </div>
            <p className="text-gray-300 leading-relaxed mb-4">
              Mic Check Millionaire reserves the right to suspend or remove accounts based on violations of this Code of Conduct. Actions we may take include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
              <li><strong>Warning:</strong> For minor or first-time violations</li>
              <li><strong>Temporary Suspension:</strong> For repeated violations or moderate offenses</li>
              <li><strong>Permanent Ban:</strong> For severe violations (hate speech, harassment, exploitation)</li>
              <li><strong>Content Removal:</strong> Inappropriate posts, comments, or uploads will be deleted</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              All enforcement decisions are made at MCM&apos;s sole discretion. We may skip warnings for serious violations.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Reporting */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">Reporting Violations</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you witness behavior that violates this Code of Conduct, please report it immediately:
            </p>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
              <p className="text-white mb-2"><strong>Report via:</strong></p>
              <p className="text-gray-300">Email: <a href="mailto:support@miccheckmillionaire.com" className="text-[#FFCC00] hover:underline">support@miccheckmillionaire.com</a></p>
              <p className="text-gray-300 mt-2">Support Form: <Link to="/support" className="text-[#FFCC00] hover:underline">Contact Support</Link></p>
            </div>
            <p className="text-gray-300 leading-relaxed mt-4">
              Include as much detail as possible: usernames, timestamps, screenshots, and a description of the incident. All reports are confidential.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Full Terms */}
          <section>
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">Additional Information</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              This Code of Conduct is part of our broader <Link to="/terms" className="text-[#FFCC00] hover:underline">Terms of Service</Link>. For account termination policies, refunds, and legal information, please review the full Terms.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Questions? Contact us at <a href="mailto:hello@miccheckmillionaire.com" className="text-[#FFCC00] hover:underline">hello@miccheckmillionaire.com</a>
            </p>
          </section>
        </Card>

        {/* Footer Links */}
        <div className="mt-12 text-center space-x-6">
          <Link to="/terms" className="text-[#FFCC00] hover:underline">Terms of Service</Link>
          <Link to="/privacy" className="text-[#FFCC00] hover:underline">Privacy Policy</Link>
          <Link to="/support" className="text-[#FFCC00] hover:underline">Contact Support</Link>
        </div>
      </div>
    </div>
  );
};

export default CodeOfConduct;